import { useState, useRef, useEffect, createContext, useContext, useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Bell, MoreVertical, UserPlus, LogOut, Pencil, Menu } from 'lucide-react'
import { clsx } from 'clsx'
import { LeagueFormat, TransferWindowState, type League } from '../types'
import { useLeagueScreen } from '../hooks/useLeagueScreen'
import {
  useLeagueGameweekStatus,
  useLeagueStandings,
  useMostPickedPlayer,
  useLeagueFixtures,
} from '../queries'
import { useAppStore, selectSession } from '@/shared/stores/appStore'
import { supabase } from '@/shared/lib/auth'
import { formatCountdown } from '@/shared/lib/formatCountdown'
import { useMyProfile } from '@/features/home/queries'
import { InfoButton } from '@/shared/components/ui/InfoButton'
import { GameweekBanner } from './GameweekBanner'
import { GameweekNavigator } from './GameweekNavigator'
import { LeagueTabs, type LeagueTab } from './LeagueTabs'
import { LeagueStandingHero, type HeroGapStat } from './LeagueStandingHero'
import { DeadlineCard } from './DeadlineCard'
import { LeagueHighlights, type HighlightItem } from './LeagueHighlights'
import { DicaDaRodadaCard } from './DicaDaRodadaCard'
import { LeagueInfoCard } from './LeagueInfoCard'
import { LeagueRulesSheet } from './LeagueRulesSheet'
import { InviteNudge } from './InviteNudge'
import { StandingsTable } from './StandingsTable'
import { StandingsTableSkeleton } from './StandingsTableSkeleton'
import { EventsFeed } from './EventsFeed'
import { FixturesPanel } from './FixturesPanel'
import { MatchdayPanel } from './MatchdayPanel'
import { MembersList } from './MembersList'
import { SquadPicker }  from '@/features/squad/components/SquadPicker'
import { NotificationsSheet } from './NotificationsSheet'
import { InviteSheet } from './InviteSheet'
import { EditTeamNameSheet } from './EditTeamNameSheet'
import styles from './LeagueScreen.module.css'

// ─── Context ──────────────────────────────────────────────────────────────────

type LeagueContextValue = { league: League; leagueId: string }
const LeagueContext = createContext<LeagueContextValue | null>(null)
export function useLeagueContext() {
  const ctx = useContext(LeagueContext)
  if (!ctx) throw new Error('useLeagueContext must be inside LeagueScreen')
  return ctx
}

// ─── Format badge ─────────────────────────────────────────────────────────────

function FormatBadge({ format }: { format: LeagueFormat }) {
  const label = format === LeagueFormat.HeadToHead ? 'Head-to-Head' : 'Pontos Gerais'
  return <span className={styles.formatBadge}>{label}</span>
}

// ─── User avatar with profile/logout dropdown ─────────────────────────────────

function deriveInitials(session: ReturnType<typeof selectSession>): string {
  if (!session) return '?'
  const fullName = (session.user.user_metadata?.['full_name'] as string | undefined) ?? ''
  if (fullName.trim()) {
    return fullName
      .trim()
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('')
  }
  return session.user.email?.[0]?.toUpperCase() ?? '?'
}

function UserAvatarButton() {
  const session   = useAppStore(selectSession)
  const setSession = useAppStore((s) => s.setSession)
  const navigate  = useNavigate()
  const { data: profile } = useMyProfile()
  const isFree    = !profile?.subscriptionTier || profile.subscriptionTier === 'FREE'
  const [open, setOpen] = useState(false)
  const wrapRef   = useRef<HTMLDivElement>(null)
  const initials  = deriveInitials(session)

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  async function handleLogout() {
    setOpen(false)
    await supabase.auth.signOut()
    setSession(null)
    void navigate({ to: '/entrar', search: { redirect: undefined, code: undefined, error: undefined } })
  }

  return (
    <div className={styles.avatarWrap} ref={wrapRef}>
      <button
        className={styles.avatar}
        onClick={() => setOpen((v) => !v)}
        aria-label="Menu do usuário"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {initials}
        {isFree && <span className={styles.tierDot} aria-hidden="true" />}
      </button>

      {open && (
        <div className={styles.dropdown} role="menu">
          <div className={styles.dropdownEmail}>{session?.user.email}</div>
          <div className={styles.dropdownDivider} />
          <button className={styles.dropdownItem} role="menuitem" onClick={handleLogout}>
            <LogOut size={15} />
            Sair da conta
          </button>
        </div>
      )}
    </div>
  )
}

// ─── Overflow menu (leave league only) ────────────────────────────────────────

type OverflowMenuProps = { league: League; leagueId: string; onEditTeamName: () => void }

function OverflowMenu({ league, onEditTeamName }: OverflowMenuProps) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  const canLeave = !league.isAdmin || league.memberCount <= 1
  // The team alias is frozen once this league's first gameweek locks (server-enforced
  // via league.IsLocked — not the competition's current GW number, which can already be
  // mid-season for leagues created in a running competition like Brasileirão).
  const teamNameLocked = league.isLocked

  return (
    <div className={styles.overflowWrap} ref={wrapRef}>
      <button
        className={styles.iconBtn}
        onClick={() => setOpen((v) => !v)}
        aria-label="Mais opções"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <MoreVertical size={18} />
      </button>

      {open && (
        <div className={styles.dropdown} role="menu">
          <button
            className={styles.dropdownItem}
            role="menuitem"
            disabled={teamNameLocked}
            title={teamNameLocked ? 'O nome do time não pode ser alterado após o início da competição' : undefined}
            onClick={() => {
              setOpen(false)
              onEditTeamName()
            }}
          >
            <Pencil size={15} />
            Editar nome do time
          </button>
          <button
            className={clsx(styles.dropdownItem, styles.dropdownLeave)}
            role="menuitem"
            disabled={!canLeave}
            title={!canLeave ? 'Promova outro membro a admin antes de sair' : undefined}
            onClick={() => {
              // TODO: leave league confirmation modal
              setOpen(false)
            }}
          >
            <LogOut size={15} />
            Sair da liga
          </button>
        </div>
      )}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

type LeagueScreenProps = {
  leagueId: string
  activeTab: string
  onTabChange: (tab: string) => void
  selectedGwId?: string
  onGwChange: (gwId: string | undefined) => void
}

export function LeagueScreen({
  leagueId,
  activeTab,
  onTabChange,
  selectedGwId,
  onGwChange,
}: LeagueScreenProps) {
  const [showNotifications, setShowNotifications] = useState(false)
  const [showInvite, setShowInvite]               = useState(false)
  const [showEditTeamName, setShowEditTeamName]   = useState(false)
  const [showLeagueRules, setShowLeagueRules]     = useState(false)
  const setMobileMenuOpen = useAppStore((s) => s.setMobileMenuOpen)

  // Keep the squad picker mounted once visited so an in-progress draft survives
  // tab switches — the draft is component-local state in useSquadPicker, so
  // unmounting it (the old `activeTab === 'escalacoes' &&` render) discards it.
  const squadVisitedRef = useRef(false)
  if (activeTab === 'escalacoes') squadVisitedRef.current = true

  // Non-standings data (mock until real APIs are wired)
  const { league, standings: mockStandings, members, fixtures: mockFixtures, events, isLoading } = useLeagueScreen(leagueId)

  // Real H2H fixtures (H2H leagues only)
  const isH2H = league.format === LeagueFormat.HeadToHead
  const { data: liveFixtures } = useLeagueFixtures(leagueId, isH2H)
  const fixtures = liveFixtures ?? mockFixtures

  // GW navigation
  const { data: gwStatus } = useLeagueGameweekStatus(leagueId)
  const selectedGwNumber =
    gwStatus?.gameweeks.find(g => g.id === selectedGwId)?.number
    ?? gwStatus?.currentGameweekNumber
    ?? league.gwStatus.currentGameweek
    ?? 1
  const currentGwNumber = gwStatus?.currentGameweekNumber ?? league.gwStatus.currentGameweek ?? 1
  const totalGws = gwStatus?.gameweeks.length ?? 8

  function handleGwPrev() {
    if (!gwStatus) return
    const prev = gwStatus.gameweeks.find(g => g.number === selectedGwNumber - 1)
    if (prev) onGwChange(prev.id)
  }

  function handleGwNext() {
    if (!gwStatus) return
    const next = gwStatus.gameweeks.find(g => g.number === selectedGwNumber + 1)
    if (next) onGwChange(next.id)
  }

  // Standings — real API with fallback to mock
  const { data: liveStandings, isLoading: standingsLoading } = useLeagueStandings(leagueId, selectedGwId)
  const standings = liveStandings ?? mockStandings

  // Most-picked player (hidden while transfer window is open)
  const { data: mostPickedPlayer } = useMostPickedPlayer(
    leagueId,
    selectedGwId,
    league.gwStatus.windowState,
  )

  // Derived mini-stats from standings (no extra fetch needed)
  const artilheiro = standings
    .filter(s => !s.isAverageParticipant)
    .sort((a, b) => b.gwPoints - a.gwPoints)[0] ?? null

  const maiorSubida = standings
    .filter((s): s is typeof s & { previousRank: number } =>
      !s.isAverageParticipant && s.previousRank !== null && s.previousRank > s.rank)
    .sort((a, b) => (b.previousRank - b.rank) - (a.previousRank - a.rank))[0] ?? null

  // Dashboard derivations (real members only — excludes the H2H "Média" ghost)
  const currentUserEntry = standings.find(s => s.isCurrentUser) ?? null
  const realMembers = standings.filter(s => !s.isAverageParticipant)
  const totalParticipants = realMembers.length || league.memberCount
  const windowCountdown = league.gwStatus.windowChangesAt
    ? formatCountdown(league.gwStatus.windowChangesAt)
    : null

  // Points gap to the neighbouring rank (lead if 1st, deficit otherwise)
  let heroGap: HeroGapStat | null = null
  if (currentUserEntry && realMembers.length >= 2) {
    const sorted = realMembers.slice().sort((a, b) => a.rank - b.rank)
    const myIdx = sorted.findIndex(s => s.isCurrentUser)
    if (myIdx > 0) {
      const above = sorted[myIdx - 1]
      if (above) heroGap = { label: `Para o ${above.rank}º`, value: `−${above.totalPoints - currentUserEntry.totalPoints}` }
    } else if (myIdx === 0) {
      const second = sorted[1]
      if (second) heroGap = { label: 'Vantagem', value: `+${currentUserEntry.totalPoints - second.totalPoints}` }
    }
  }

  const tabs = useMemo<LeagueTab[]>(() => {
    if (league.format === LeagueFormat.HeadToHead) {
      return [
        { id: 'tabela',      label: 'Classificação' },
        { id: 'partidas',    label: 'Partidas'      },
        { id: 'confrontos',  label: 'Confrontos'    },
        { id: 'eventos',     label: 'Eventos'       },
        { id: 'escalacoes',  label: 'Sua Escalação' },
      ]
    }
    return [
      { id: 'tabela',     label: 'Classificação' },
      { id: 'partidas',   label: 'Partidas'      },
      { id: 'eventos',    label: 'Eventos'       },
      { id: 'escalacoes', label: 'Sua Escalação' },
    ]
  }, [league.format])

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.skeleton} />
        <div className={styles.skeleton} style={{ width: '60%' }} />
      </div>
    )
  }

  const showMostPickedCard =
    mostPickedPlayer !== null &&
    mostPickedPlayer !== undefined &&
    league.gwStatus.windowState === TransferWindowState.Closed

  const goToSquad = () => onTabChange('escalacoes')

  // "Destaques da liga" rail card — only meaningful with 2+ real members
  const highlightItems: HighlightItem[] = []
  if (realMembers.length >= 2) {
    if (artilheiro) {
      highlightItems.push({ icon: '🏆', label: 'Artilheiro da rodada', value: artilheiro.teamName, sub: `${artilheiro.gwPoints} pts` })
    }
    if (maiorSubida) {
      highlightItems.push({ icon: '📈', label: 'Maior subida', value: maiorSubida.teamName, sub: `+${maiorSubida.previousRank - maiorSubida.rank} posições` })
    }
    if (showMostPickedCard) {
      highlightItems.push({
        icon: '👥',
        label: 'Mais escalado',
        value: mostPickedPlayer.name,
        sub: `${Math.round(mostPickedPlayer.pickPercentage * 100)}% das equipes · ${mostPickedPlayer.gwPoints ?? 0} pts`,
        accent: true,
      })
    }
  }

  const showInviteNudge = league.isAdmin && league.memberCount < 3 && !!league.joinCode

  const gwNav = (
    <GameweekNavigator
      selectedGwNumber={selectedGwNumber}
      currentGwNumber={currentGwNumber}
      totalGws={totalGws}
      onPrev={handleGwPrev}
      onNext={handleGwNext}
      isLoading={standingsLoading}
    />
  )

  return (
    <LeagueContext.Provider value={{ league, leagueId }}>
      <div className={styles.screen}>
        {/* ── Header ─────────────────────────────────────── */}
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            {/* Mobile-only hamburger — opens the sidebar drawer. Hidden at 1024px+. */}
            <button
              type="button"
              className={styles.menuButton}
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Abrir menu"
            >
              <Menu size={22} aria-hidden="true" />
            </button>
            <h1 className={styles.leagueName}>{league.name}</h1>
            {/* GW navigator — desktop only; mobile shows below tabs */}
            <div className={styles.headerGwNav}>{gwNav}</div>
            <FormatBadge format={league.format} />
          </div>
          <div className={styles.headerActions}>
            <InfoButton label="Ver regras da liga" onClick={() => setShowLeagueRules(true)} />
            {league.isAdmin && (
              <button
                className={styles.iconBtn}
                onClick={() => setShowInvite(true)}
                aria-label="Convidar membros"
                title="Convidar membros"
              >
                <UserPlus size={18} />
              </button>
            )}
            <button
              className={styles.iconBtn}
              onClick={() => setShowNotifications(true)}
              aria-label="Notificações"
            >
              <Bell size={18} />
            </button>
            <OverflowMenu league={league} leagueId={leagueId} onEditTeamName={() => setShowEditTeamName(true)} />
            <UserAvatarButton />
          </div>
        </header>

        {/* ── Gameweek banner ──────────────────────────────────── */}
        {/* On the standings tab the deadline lives in the rail (DeadlineCard) */}
        {activeTab !== 'tabela' && <GameweekBanner gwStatus={league.gwStatus} />}

        {/* ── Tab navigation ───────────────────────────────────── */}
        <LeagueTabs tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} />

        {/* GW navigator — mobile only; desktop shows in header */}
        {(activeTab === 'tabela' || activeTab === 'partidas' || activeTab === 'escalacoes') && (
          <div className={styles.tabsGwNavBar}>{gwNav}</div>
        )}

        {/* ── Tab content ──────────────────────────────────────── */}
        <div className={styles.content}>
          {activeTab === 'tabela' && (
            <div className={styles.dashGrid}>
              {/* Primary column — your standing + the table */}
              <div className={styles.dashMain}>
                {standingsLoading ? (
                  <StandingsTableSkeleton />
                ) : (
                  <>
                    <LeagueStandingHero
                      entry={currentUserEntry}
                      totalParticipants={totalParticipants}
                      gap={heroGap}
                      isH2H={isH2H}
                      windowCountdown={windowCountdown}
                      onEditSquad={goToSquad}
                    />

                    <div className={styles.tableBlock}>
                      <div className={styles.secLabel}>
                        <span className={styles.secLabelT}>Classificação</span>
                        <span className={styles.secLabelS}>· Rodada {selectedGwNumber}</span>
                      </div>
                      <div className={styles.tableCard}>
                        <StandingsTable league={league} standings={standings} />
                      </div>
                    </div>

                    {showInviteNudge && <InviteNudge joinCode={league.joinCode} />}
                  </>
                )}
              </div>

              {/* Secondary rail — deadline, highlights, tips, league info */}
              <div className={styles.dashRail}>
                <DeadlineCard gwStatus={league.gwStatus} onAction={goToSquad} />
                <LeagueHighlights items={highlightItems} />
                <DicaDaRodadaCard leagueId={leagueId} />
                <LeagueInfoCard league={league} />
              </div>
            </div>
          )}
          {activeTab === 'partidas' && (
            <MatchdayPanel
              gameweekId={selectedGwId ?? gwStatus?.currentGameweekId}
              gwNumber={selectedGwNumber}
              gwStatus={league.gwStatus}
              onAction={goToSquad}
            />
          )}
          {activeTab === 'confrontos' && league.format === LeagueFormat.HeadToHead && (
            <FixturesPanel league={league} fixtures={fixtures} />
          )}
          {activeTab === 'eventos' && (
            <EventsFeed events={events} />
          )}
          {squadVisitedRef.current && (() => {
            const currentUserStanding = standings.find(s => s.isCurrentUser) ?? null
            return (
              <div className={clsx(activeTab !== 'escalacoes' && styles.tabHidden)}>
                <SquadPicker
                  leagueId={leagueId}
                  selectedGwId={selectedGwId}
                  standingGwPoints={currentUserStanding?.gwPoints}
                  standingTotalPoints={currentUserStanding?.totalPoints}
                />
              </div>
            )
          })()}
        </div>
      </div>

      {showLeagueRules && (
        <LeagueRulesSheet league={league} onClose={() => setShowLeagueRules(false)} />
      )}
      {showNotifications && (
        <NotificationsSheet league={league} onClose={() => setShowNotifications(false)} />
      )}
      {showInvite && (
        <InviteSheet league={league} leagueId={leagueId} onClose={() => setShowInvite(false)} />
      )}
      {showEditTeamName && (
        <EditTeamNameSheet
          leagueId={leagueId}
          currentName={standings.find((s) => s.isCurrentUser)?.teamName ?? ''}
          onClose={() => setShowEditTeamName(false)}
        />
      )}
    </LeagueContext.Provider>
  )
}
